import { reviewResourceTypeEnum, reviewStatusEnum } from "@crm/db/schema/fulfillment";
import { requiredString, withParse } from "@crm/effect/schema";
import { Schema } from "effect";

const identifierSchema = Schema.String;
const nullableStringSchema = Schema.NullOr(Schema.String);
const nullableNumberSchema = Schema.NullOr(Schema.Finite);
const reviewStatusSchema = Schema.Literals([...reviewStatusEnum.enumValues]);
const reviewResourceTypeSchema = Schema.Literals([...reviewResourceTypeEnum.enumValues]);

const entityIdSchemaSource = Schema.Struct({ id: identifierSchema });
const courseIdSchemaSource = Schema.Struct({ courseId: identifierSchema });
const moduleIdSchemaSource = Schema.Struct({ moduleId: identifierSchema });

export const undefinedInputSchema = withParse(Schema.Undefined);

export const entityIdInputSchema = withParse(entityIdSchemaSource);
export type EntityIdInput = Schema.Schema.Type<typeof entityIdInputSchema>;

export const courseIdInputSchema = withParse(courseIdSchemaSource);
export type CourseIdInput = Schema.Schema.Type<typeof courseIdInputSchema>;

export const moduleIdInputSchema = withParse(moduleIdSchemaSource);
export type ModuleIdInput = Schema.Schema.Type<typeof moduleIdInputSchema>;

export const getReviewCoursesInputSchema = withParse(
  Schema.UndefinedOr(Schema.Struct({ status: Schema.optional(reviewStatusSchema) })),
);
export type GetReviewCoursesInput = Schema.Schema.Type<typeof getReviewCoursesInputSchema>;

const reviewCourseChangesSchema = Schema.Struct({
  name: Schema.optional(Schema.String),
  description: Schema.optional(nullableStringSchema),
  thumbnail: Schema.optional(nullableStringSchema),
  courseId: Schema.optional(nullableStringSchema),
  status: Schema.optional(reviewStatusSchema),
  order: Schema.optional(Schema.Finite),
});

export const createReviewCourseInputSchema = withParse(
  Schema.Struct({
    id: identifierSchema,
    name: Schema.String,
    description: Schema.optional(nullableStringSchema),
    thumbnail: Schema.optional(nullableStringSchema),
    courseId: Schema.optional(nullableStringSchema),
    status: Schema.optional(reviewStatusSchema),
    order: Schema.optional(Schema.Finite),
  }),
);
export type CreateReviewCourseInput = Schema.Schema.Type<typeof createReviewCourseInputSchema>;

export const updateReviewCourseInputSchema = withParse(
  Schema.Struct({ id: identifierSchema, changes: reviewCourseChangesSchema }),
);
export type UpdateReviewCourseInput = Schema.Schema.Type<typeof updateReviewCourseInputSchema>;

const reviewModuleChangesSchema = Schema.Struct({
  courseId: Schema.optional(identifierSchema),
  name: Schema.optional(Schema.String),
  description: Schema.optional(nullableStringSchema),
  order: Schema.optional(Schema.Finite),
});

export const createReviewModuleInputSchema = withParse(
  Schema.Struct({
    id: identifierSchema,
    courseId: identifierSchema,
    name: Schema.String,
    description: Schema.optional(nullableStringSchema),
    order: Schema.optional(Schema.Finite),
  }),
);
export type CreateReviewModuleInput = Schema.Schema.Type<typeof createReviewModuleInputSchema>;

export const updateReviewModuleInputSchema = withParse(
  Schema.Struct({ id: identifierSchema, changes: reviewModuleChangesSchema }),
);
export type UpdateReviewModuleInput = Schema.Schema.Type<typeof updateReviewModuleInputSchema>;

const reviewLessonChangesSchema = Schema.Struct({
  moduleId: Schema.optional(identifierSchema),
  name: Schema.optional(Schema.String),
  description: Schema.optional(nullableStringSchema),
  videoId: Schema.optional(nullableStringSchema),
  duration: Schema.optional(nullableNumberSchema),
  status: Schema.optional(reviewStatusSchema),
  order: Schema.optional(Schema.Finite),
});

export const createReviewLessonInputSchema = withParse(
  Schema.Struct({
    id: identifierSchema,
    moduleId: identifierSchema,
    name: Schema.String,
    description: Schema.optional(nullableStringSchema),
    videoId: Schema.optional(nullableStringSchema),
    duration: Schema.optional(nullableNumberSchema),
    status: Schema.optional(reviewStatusSchema),
    order: Schema.optional(Schema.Finite),
  }),
);
export type CreateReviewLessonInput = Schema.Schema.Type<typeof createReviewLessonInputSchema>;

export const updateReviewLessonInputSchema = withParse(
  Schema.Struct({ id: identifierSchema, changes: reviewLessonChangesSchema }),
);
export type UpdateReviewLessonInput = Schema.Schema.Type<typeof updateReviewLessonInputSchema>;

export const batchReorderLessonsInputSchema = withParse(
  Schema.Struct({
    items: Schema.mutable(
      Schema.Array(Schema.Struct({ id: identifierSchema, order: Schema.Finite })),
    ),
  }),
);
export type BatchReorderLessonsInput = Schema.Schema.Type<typeof batchReorderLessonsInputSchema>;

export const getReviewResourcesInputSchema = withParse(
  Schema.Struct({ courseId: Schema.optional(identifierSchema) }),
  undefined,
  (input) => input ?? {},
);
export type GetReviewResourcesInput = Schema.Schema.Type<typeof getReviewResourcesInputSchema>;

const reviewResourceFields = {
  courseId: Schema.optional(nullableStringSchema),
  moduleId: Schema.optional(nullableStringSchema),
  lessonId: Schema.optional(nullableStringSchema),
  title: Schema.optional(Schema.String),
  description: Schema.optional(nullableStringSchema),
  type: Schema.optional(reviewResourceTypeSchema),
  url: Schema.optional(nullableStringSchema),
  fileSize: Schema.optional(nullableNumberSchema),
  order: Schema.optional(Schema.Finite),
} as const;

export const createReviewResourceInputSchema = withParse(
  Schema.Struct({
    id: identifierSchema,
    ...reviewResourceFields,
    title: Schema.String,
  }),
);
export type CreateReviewResourceInput = Schema.Schema.Type<typeof createReviewResourceInputSchema>;

export const updateReviewResourceInputSchema = withParse(
  Schema.Struct({
    id: identifierSchema,
    changes: Schema.Struct(reviewResourceFields),
  }),
);
export type UpdateReviewResourceInput = Schema.Schema.Type<typeof updateReviewResourceInputSchema>;

export const getLessonProgressInputSchema = withParse(
  Schema.Struct({
    lessonIds: Schema.optional(Schema.mutable(Schema.Array(identifierSchema))),
  }),
  undefined,
  (input) => input ?? {},
);
export type GetLessonProgressInput = Schema.Schema.Type<typeof getLessonProgressInputSchema>;

const progressCounterSchema = Schema.Finite.pipe(
  Schema.check(Schema.isInt()),
  Schema.check(Schema.isGreaterThanOrEqualTo(0)),
);
const progressPercentageSchema = progressCounterSchema.pipe(
  Schema.check(Schema.isLessThanOrEqualTo(100)),
);

export const saveLessonProgressInputSchema = withParse(
  Schema.Struct({
    lessonId: identifierSchema,
    watchedSeconds: progressCounterSchema,
    lastPosition: progressCounterSchema,
    progressPercentage: progressPercentageSchema,
    completed: Schema.Boolean,
  }),
);
export type SaveLessonProgressInput = Schema.Schema.Type<typeof saveLessonProgressInputSchema>;

export const clearLessonProgressInputSchema = withParse(
  Schema.Struct({ lessonId: identifierSchema }),
);
export type ClearLessonProgressInput = Schema.Schema.Type<typeof clearLessonProgressInputSchema>;

export const getCourseProgressInputSchema = withParse(
  Schema.Struct({ courseId: requiredString("courseId 不能为空") }),
);
export type GetCourseProgressInput = Schema.Schema.Type<typeof getCourseProgressInputSchema>;

export const getCourseProgressBatchInputSchema = withParse(
  Schema.Struct({
    courseIds: Schema.mutable(Schema.Array(requiredString("courseId 不能为空"))).pipe(
      Schema.check(Schema.isMinLength(1)),
    ),
  }),
);
export type GetCourseProgressBatchInput = Schema.Schema.Type<
  typeof getCourseProgressBatchInputSchema
>;
